import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { Base_URL } from "../../actions/Base_URL";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Email, 2: OTP & New Password
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [role, setRole] = useState("student");

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error("Please enter your email");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${Base_URL}/user/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, role }),
            });
            const data = await response.json();

            if (response.ok) {
                toast.success(data.message || "OTP sent successfully!");
                setStep(2);
            } else {
                toast.error(data.message || "Failed to send OTP");
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!otp || !newPassword || !confirmPassword) {
            toast.error("All fields are required");
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        if (newPassword.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${Base_URL}/user/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp, newPassword, role }),
            });
            const data = await response.json();

            if (response.ok) {
                toast.success("Password reset successfully! Redirecting to login...");
                setTimeout(() => {
                    navigate("/auth/login");
                }, 2000);
            } else {
                toast.error(data.message || "Failed to reset password");
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toaster position="bottom-left" reverseOrder={false} />
            <main>
                <section className="relative w-full sm:py-3 min-h-screen bg-dark-primary">
                    <div className="relative w-full min-h-screen flex items-center justify-center bg-dark-primary">
                        <div className="absolute top-0 w-full h-full bg-dark-primary" style={{ zIndex: "0" }}></div>

                        <div className="w-full lg:w-4/12 px-8 relative z-10">
                            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-lg bg-dark-card border border-dark-tertiary shadow-2xl">
                                <div className="flex-auto px-4 lg:px-10 py-10 pt-8">
                                    <div className="text-gray-100 text-center mb-8 font-bold text-2xl">
                                        {step === 1 ? "Forgot Password" : "Reset Password"}
                                    </div>

                                    {step === 1 ? (
                                        <form onSubmit={handleSendOtp}>
                                            <div className="relative w-full mb-6">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">
                                                    Select Role <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={role}
                                                    onChange={(e) => setRole(e.target.value)}
                                                    className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary w-full transition-all duration-150"
                                                >
                                                    <option value="student">Student</option>
                                                    <option value="admin">Admin</option>
                                                    <option value="lab_incharge">Lab Incharge</option>
                                                </select>
                                            </div>
                                            <div className="relative w-full mb-6">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">
                                                    Email Address <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary w-full transition-all duration-150"
                                                    placeholder="Enter your registered email"
                                                />
                                            </div>
                                            <div className="text-center mt-6">
                                                <button
                                                    className="bg-accent-primary text-white active:bg-accent-hover text-sm font-bold uppercase px-6 py-3 rounded-lg shadow hover:shadow-lg hover:bg-accent-hover outline-none focus:outline-none w-full transition-all duration-150"
                                                    type="submit"
                                                    disabled={loading}
                                                >
                                                    {loading ? "Sending OTP..." : "Send OTP"}
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <form onSubmit={handleResetPassword}>
                                            <div className="relative w-full mb-4">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">
                                                    Enter OTP <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={otp}
                                                    onChange={(e) => setOtp(e.target.value)}
                                                    className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary w-full"
                                                    placeholder="Enter OTP sent to email"
                                                />
                                            </div>
                                            <div className="relative w-full mb-4">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">
                                                    New Password <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary w-full"
                                                    placeholder="Min 8 characters"
                                                />
                                            </div>
                                            <div className="relative w-full mb-6">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">
                                                    Confirm Password <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="border border-dark-tertiary px-4 py-3 placeholder-gray-500 text-gray-100 bg-dark-input rounded-lg text-sm shadow focus:outline-none focus:ring-2 focus:ring-accent-primary w-full"
                                                    placeholder="Re-enter password"
                                                />
                                            </div>
                                            <div className="text-center mt-6">
                                                <button
                                                    className="bg-accent-primary text-white active:bg-accent-hover text-sm font-bold uppercase px-6 py-3 rounded-lg shadow hover:shadow-lg hover:bg-accent-hover outline-none focus:outline-none w-full transition-all duration-150"
                                                    type="submit"
                                                    disabled={loading}
                                                >
                                                    {loading ? "Resetting..." : "Reset Password"}
                                                </button>
                                            </div>
                                        </form>
                                    )}

                                    <div className="mt-6 text-center">
                                        <Link to="/auth/login" className="text-gray-400 hover:text-accent-primary font-bold text-sm">
                                            Back to Login
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
